import React, { useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import {
  exportPayments,
  exportInvoiceAdvanceDenormalizedEntries,
} from 'provider/queries/hasuraQueries'
import { Button, useGetIdentity, useTranslate } from 'react-admin'
import { downloadFiles } from 'utils/axios'

import {
  white,
  orange,
  darkGray,
  lightergray3,
  orangeSemitransparent,
} from '../../constants/colorConstants'

const useStyles = makeStyles((theme) => ({
  exportContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  button: {
    paddingTop: '16px',
    paddingBottom: '16px',
    paddingLeft: '29px',
    paddingRight: '29px',
    borderRadius: '28px',
    backgroundColor: 'transparent',
    ...theme.mundi.font.roboto700Normal,
    color: orange,
    fontSize: '13px',
    lineHeight: '15.23px',
    marginTop: '-40px',
    marginRight: '34px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  fullScreen: {
    position: 'fixed',
    zIndex: 10000,
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
  },
  dropDown: {
    position: 'relative',
    zIndex: 10000,
    height: '1px',
    paddingTop: '16px',
    paddingBottom: '16px',
    marginTop: '-15px',
    marginRight: '30px',
  },
  dropDownContainer: {
    ...theme.mundi.font.roboto600Normal,
    fontSize: '13px',
    lineHeight: '15.23px',
    borderRadius: '4px',
    backgroundColor: white,
    color: darkGray,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    width: '187px',
    borderBottom: `solid 1px ${lightergray3}`,
  },
  innerButton: {
    padding: '16px 24px',
    ...theme.mundi.font.roboto400Normal,
    background: white,
    color: darkGray,
    fontSize: '13px',
    lineHeight: '15.23px',
    borderTop: `solid 1px ${lightergray3}`,
    borderLeft: `solid 1px ${lightergray3}`,
    borderRight: `solid 1px ${lightergray3}`,
    borderRadius: 0,
    display: 'block',
    textAlign: 'left',
    '&:hover': {
      borderLeft: `solid 3px ${orange}`,
      backgroundColor: orangeSemitransparent,
    },
    '& span': {
      textTransform: 'none',
    },
  },
}))

export const ExportPaymentActionButton = ({
  label,
  fields,
  basePath,
  onExport,
  ...props
}) => {
  const classes = useStyles()
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const { identity } = useGetIdentity()
  const { enqueueSnackbar } = useSnackbar()
  const translate = useTranslate()

  const handleClick = () => {
    setIsDropdownVisible(true)
  }

  const handleCancel = () => {
    setIsDropdownVisible(false)
  }

  const handleClose = async (modes, name) => {
    setIsDropdownVisible(false)

    let fileName = ''
    const today = new Date()
    const date = `${today.getFullYear().toString().padStart(4, '0')}${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`
    if (modes.includes('payments') && !modes.includes('collections')) {
      fileName = `${date} - ${identity.companyName} - Payments Detailed Statement.xlsx`
    } else if (modes.includes('collections') && !modes.includes('payments')) {
      fileName = `${date} - ${identity.companyName} - Collections Detailed Statement.xlsx`
    } else if (modes.includes('collections') && modes.includes('payments')) {
      fileName = `${date} - ${identity.companyName} - Payments Collections Detailed Statement.xlsx`
    }

    const downloadResponse = await downloadFiles(
      {
        queries: {
          payments: exportPayments(identity.id, name),
          entries: exportInvoiceAdvanceDenormalizedEntries(identity.id),
        },
        modes,
      },
      fileName,
      'payments'
    )

    if (downloadResponse?.error) {
      enqueueSnackbar(
        `${translate('resources.payments.actions.export.error')}`,
        { autoHideDuration: 3000, variant: 'error' }
      )
    }

    if (onExport) onExport(modes, name)
  }

  return (
    <div className={classes.exportContainer}>
      <Button
        {...props}
        className={classes.button}
        icon={<></>}
        label={label}
        onClick={handleClick}
      />
      {isDropdownVisible && (
        <div
          role="button"
          tabIndex={0}
          className={classes.fullScreen}
          onClick={handleCancel}
        />
      )}
      {isDropdownVisible && (
        <div className={classes.dropDown}>
          <div className={classes.dropDownContainer}>
            {fields.map((item) => (
              <Button
                key={`export${item.id}`}
                className={classes.innerButton}
                onClick={() => handleClose(item.id, item.name)}
              >
                <>{item.name}</>
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
